import { useState } from 'react';
import type { BatchItem } from '../types';
import { LOCALE_META } from '../lib/locales';

export default function Batch({
  items, onRemove, onClear, onSubmit, justSent, onKeepGoing,
}: {
  items: BatchItem[];
  onRemove: (item: BatchItem) => void;
  onClear: () => void;
  onSubmit: (note: string | null) => Promise<void>;
  justSent: number;
  onKeepGoing: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (justSent > 0) {
    return (
      <>
        <div className="phead"><div><h1>Batch sent</h1></div></div>
        <div className="sent">
          <b>{justSent} item{justSent === 1 ? '' : 's'} on their way to Sam</b>
          <p>
            He reviews every batch before anything reaches the app. You will see the outcome under
            Already sent — no need to wait here.
          </p>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button className="btn pri" onClick={onKeepGoing}>Keep reviewing</button>
          </div>
        </div>
      </>
    );
  }

  if (!items.length) {
    return (
      <>
        <div className="phead"><div><h1>My batch</h1>
          <p>Everything you decide collects here first. You look it over, then send it in one go.</p>
        </div></div>
        <div className="panel"><div className="emptycart">
          <b>Your batch is empty</b>
          <p>Work through a queue — mark the good ones, correct the wrong ones, ask about the
            confusing ones. It all lands here for one last look.</p>
          <button className="btn pri" onClick={onKeepGoing}>Go to the review queue</button>
        </div></div>
      </>
    );
  }

  const section = (kind: BatchItem['kind'], title: string, desc: string) => {
    const rows = items.filter((i) => i.kind === kind);
    if (!rows.length) return null;
    const tone = kind === 'fix' ? 'p-acc' : kind === 'ok' ? 'p-ok' : 'p-warn';
    return (
      <div className="bsec" key={kind}>
        <div className="bsec-h">
          <span className={`pill ${tone}`}>{rows.length}</span>
          <span>{title}</span><span className="d">{desc}</span>
        </div>
        {rows.map((i) => (
          <div className="bitem" key={`${i.unit_id}@${i.locale}`}>
            <span className="body">
              <span className="rmeta">
                <span className="rid">{i.unit_id.split('/')[1] ?? i.unit_id}</span>
                <span className="right">
                  <span className="pill p-acc">{LOCALE_META[i.locale]?.native ?? i.locale}</span>
                </span>
              </span>
              {kind === 'fix' && (
                <span className="diff">
                  <span className="o">{i.from_text}</span>
                  <span className="n">{i.to_text}</span>
                </span>
              )}
              {kind === 'question' && <span className="why">“{i.note}”</span>}
              {kind === 'ok' && (
                <span className="con" style={{ fontSize: 12, color: 'var(--muted)' }}>
                  No change — you read it and it was right.
                </span>
              )}
            </span>
            <button className="x" onClick={() => onRemove(i)}>Remove</button>
          </div>
        ))}
      </div>
    );
  };

  async function send() {
    setBusy(true); setError(null);
    try { await onSubmit(note.trim() || null); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="phead"><div><h1>My batch</h1>
        <p>Everything you have decided so far. Pull anything out before you send — this is the last stop.</p>
      </div></div>

      {section('fix', 'Text you changed', 'Sam sees the before and after')}
      {section('question', 'Questions you raised', 'His answer appears on the prompt, under Notes')}
      {section('ok', 'Confirmed correct', 'No text changes — your sign-off that these read right')}

      <div className="field" style={{ marginTop: 6 }}>
        <label htmlFor="bn">Anything to tell him? (optional)</label>
        <textarea id="bn" className="note" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. the Spicy deck leans very formal in Spanish throughout" />
      </div>

      {error && <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>}

      <div className="cartbar">
        <span className="tally"><span>{items.length} <i>item{items.length === 1 ? '' : 's'} in this batch</i></span></span>
        <span className="spacer" style={{ flex: 1 }} />
        <button className="btn" onClick={onClear} disabled={busy}>Empty batch</button>
        <button className="btn pri" onClick={() => void send()} disabled={busy}>
          {busy ? 'Sending…' : `Send ${items.length} item${items.length === 1 ? '' : 's'} to Sam`}
        </button>
      </div>
    </>
  );
}
